import { capitalize } from 'lodash';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, ModalUI, usePortal } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import { getAudienceGender, sortAgeEntries, toDistributionEntries } from '../audienceDistribution.helpers';
import { getCampaignOtaWithoutEvent, getOnTargetWithEventPct } from '../eventsOta.helpers';

import type { DoohReportEvent } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';
import type { AudienceDistributionEntry } from '../audienceDistribution.helpers';

import './eventAudienceModal.scss';

type ModalHandler = { closeModal: () => void; openModal: () => void };

type Props = {
  id: string;
  /** Event shown, or null when the modal is closed. */
  event: DoohReportEvent | null;
  /** On-target share of an untargeted plan with the same spend (0-100). */
  baselineOnTargetPct: number | null;
  /** Campaign OTA, the plan figure the event moves. */
  campaignOta: number | null;
  onClose: () => void;
  onOpenOtaInfo: () => void;
};

/**
 * "Event audience details" modal: who the event gathered (estimated audience, gender
 * and age), how concentrated the target was during it (event lift) and what it added to the campaign.
 */
export default function EventAudienceModal({
  id,
  event,
  baselineOnTargetPct,
  campaignOta,
  onClose,
  onOpenOtaInfo
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatCount, formatOta, formatPercent, formatShortDate } = useDoohReportFormatters();
  const { themeContrast } = useTheme() as { themeContrast: 'light' | 'dark' | 'darkAI' };
  const modalRef = useRef<ModalHandler>(null);
  const isOpen = event !== null;

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.openModal();
    } else {
      modalRef.current?.closeModal();
    }
  }, [isOpen]);

  const genderEntries = toDistributionEntries(event?.audienceDetails?.genderDistribution ?? null);
  const ageEntries = sortAgeEntries(toDistributionEntries(event?.audienceDetails?.ageDistribution ?? null));
  const genderLabels = {
    female: formatMessage({ defaultMessage: 'Female', id: 'doohReport.contextEvents.audienceDetails.female' }),
    male: formatMessage({ defaultMessage: 'Male', id: 'doohReport.contextEvents.audienceDetails.male' })
  };
  const getGenderLabel = (bucket: string): string => {
    const gender = getAudienceGender(bucket);
    return gender ? genderLabels[gender] : capitalize(bucket);
  };
  const distributionHeader = formatMessage({
    defaultMessage: 'Distribution (%)',
    id: 'doohReport.contextEvents.audienceDetails.distribution'
  });

  const renderTable = (title: string, entries: AudienceDistributionEntry[], getLabel = (bucket: string) => bucket) =>
    entries.length > 0 && (
      <div className="eventAudienceModal__table">
        <div className="eventAudienceModal__tableHeader">
          <span>{title}</span>
          <span>{distributionHeader}</span>
        </div>
        {entries.map(entry => (
          <div className="eventAudienceModal__tableRow" key={entry.bucket}>
            <span>{getLabel(entry.bucket)}</span>
            <span>{formatPercent(entry.share * 100)}</span>
          </div>
        ))}
      </div>
    );

  const eventName =
    event?.name ?? formatMessage({ defaultMessage: 'Untitled event', id: 'doohReport.contextEvents.untitled' });
  const withEventPct = getOnTargetWithEventPct(baselineOnTargetPct, event?.ota ?? null);
  const campaignOtaWithoutEvent = getCampaignOtaWithoutEvent(campaignOta, event?.onTargetUpliftPct ?? null);
  const eventInfo = [
    eventName,
    event?.locationLabel,
    event ? `${formatShortDate(event.startDate)} – ${formatShortDate(event.endDate)}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  const modal = ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={themeContrast}>
      <ModalUI
        className="eventAudienceModal"
        closeModalCallback={onClose}
        id={id}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Event audience details',
          id: 'doohReport.contextEvents.audienceDetails.title'
        })}
        width="770px"
      >
        {event && (
          <div className="eventAudienceModal__content">
            <div className="eventAudienceModal__intro">
              <strong>{eventInfo}.</strong>
              <span>
                {formatMessage({
                  defaultMessage:
                    'Who your ads reached during the event and how much the event boosted your on-target audience.',
                  id: 'doohReport.contextEvents.audienceDetails.intro'
                })}
              </span>
            </div>

            <div className="eventAudienceModal__distributions">
              <div className="eventAudienceModal__column">
                <div className="eventAudienceModal__table">
                  <div className="eventAudienceModal__tableHeader">
                    <span>
                      {formatMessage({
                        defaultMessage: 'Estimated audience range',
                        id: 'doohReport.contextEvents.audienceDetails.estimatedAudience'
                      })}
                    </span>
                  </div>
                  <div className="eventAudienceModal__tableRow">
                    <span>
                      {formatMessage({
                        defaultMessage: 'Individuals',
                        id: 'doohReport.contextEvents.audienceDetails.individuals'
                      })}
                    </span>
                    <span>{formatCount(event.estimatedAudience)}</span>
                  </div>
                </div>
                {renderTable(
                  formatMessage({ defaultMessage: 'Gender', id: 'doohReport.contextEvents.audienceDetails.gender' }),
                  genderEntries,
                  getGenderLabel
                )}
              </div>
              <div className="eventAudienceModal__column">
                {renderTable(
                  formatMessage({ defaultMessage: 'Age', id: 'doohReport.contextEvents.audienceDetails.age' }),
                  ageEntries
                )}
              </div>
            </div>

            <div className="eventAudienceModal__ota">
              <div className="eventAudienceModal__otaHeader">
                <span>
                  <i className="fa-solid fa-bullseye-arrow eventAudienceModal__otaIcon" />
                  {formatMessage({
                    defaultMessage: 'Event lift vs your audience',
                    id: 'doohReport.contextEvents.audienceDetails.eventLiftTitle'
                  })}
                </span>
                <span>
                  {formatMessage({
                    defaultMessage: 'Untargeted plan (same spend)',
                    id: 'doohReport.contextEvents.audienceDetails.untargetedPlan'
                  })}
                </span>
                <span>
                  {formatMessage({
                    defaultMessage: 'With the event',
                    id: 'doohReport.contextEvents.audienceDetails.withEvent'
                  })}
                </span>
                <span>
                  {formatMessage({
                    defaultMessage: 'Event lift',
                    id: 'doohReport.contextEvents.audienceDetails.eventLift'
                  })}
                </span>
              </div>
              <div className="eventAudienceModal__otaRow">
                <span>
                  {formatMessage({
                    defaultMessage: 'Impacts on your Target Audience',
                    id: 'doohReport.contextEvents.audienceDetails.impactsOnTarget'
                  })}
                </span>
                <span>{formatPercent(baselineOnTargetPct)}</span>
                <span>{formatPercent(withEventPct)}</span>
                <span className="eventAudienceModal__eventOta">{formatOta(event.ota)}</span>
              </div>
            </div>

            {event.ota !== null && (
              <p className="eventAudienceModal__text">
                {formatMessage(
                  {
                    defaultMessage:
                      'During {event}, {withEvent} of your impacts reached your Target Audience vs {baseline} with an Untargeted plan (same spend). Event lift {eventLift}.',
                    id: 'doohReport.contextEvents.audienceDetails.eventLiftSummary'
                  },
                  {
                    baseline: formatPercent(baselineOnTargetPct),
                    event: eventName,
                    eventLift: formatOta(event.ota),
                    withEvent: formatPercent(withEventPct)
                  }
                )}
              </p>
            )}
            {event.reachAddedPoints !== null && event.reachAddedPoints > 0 && (
              <p className="eventAudienceModal__text">
                {campaignOtaWithoutEvent !== null && campaignOta !== null
                  ? formatMessage(
                      {
                        defaultMessage:
                          'The event added {people} people of your Target Audience to the campaign: including it moves the campaign OTA from {without} to {with}.',
                        id: 'doohReport.contextEvents.audienceDetails.planImpact'
                      },
                      {
                        people: formatCount(event.reachAddedPoints),
                        with: formatOta(campaignOta),
                        without: formatOta(campaignOtaWithoutEvent)
                      }
                    )
                  : formatMessage(
                      {
                        defaultMessage: 'The event added {people} people of your Target Audience to the campaign.',
                        id: 'doohReport.contextEvents.audienceDetails.targetPeopleAdded'
                      },
                      { people: formatCount(event.reachAddedPoints) }
                    )}
              </p>
            )}
            <p className="eventAudienceModal__text">
              {formatMessage({
                defaultMessage:
                  'Audience figures are modelled from mobile SDK & bidstream signals during the event dates.',
                id: 'doohReport.contextEvents.audienceDetails.note'
              })}
            </p>

            <div className="eventAudienceModal__footer">
              <button className="eventAudienceModal__link" onClick={onOpenOtaInfo} type="button">
                {formatMessage({
                  defaultMessage: 'What is OTA?',
                  id: 'doohReport.contextEvents.audienceDetails.whatIsOta'
                })}
              </button>
              <ButtonUI
                id={`${id}-closeButton`}
                onClick={onClose}
                text={formatMessage({ defaultMessage: 'Close', id: 'doohReport.contextEvents.audienceDetails.close' })}
                type="secondary"
              />
            </div>
          </div>
        )}
      </ModalUI>
    </CustomThemeProvider>,
    usePortal(id)
  );

  return isOpen ? modal : null;
}
